import { refundsToGiftCard } from '@/helpers/gift-card';
import {
  OCCURRENCE_REPLACED_CODES,
  OCCURRENCE_UNAVAILABLE_CODES,
} from '@/lib/constants';
import { API_URL } from '@/lib/env';
import { getErrorCode, getErrorStatus } from '@/lib/error-utils';
import type { Order } from '@/lib/types';
import type { OrderType } from '@/helpers/order-conversion';
import { formatAmount } from '@/lib/utils';

type OrderItem = NonNullable<Order['class_order_items']>[number];

// A guest proves a receipt with its token; a signed-in owner needs none. The
// spot keeps a receipt that spans several class times on the one the customer
// was acting on: by its slot and start time, the time alone still finding it
// when a move replaced the slot.
export const orderReceiptPath = (
  orderId: string,
  receiptToken?: string | null,
  spot?: { slotId?: string | null; startTime?: string | null },
  outcome?: 'cancelled' | 'rescheduled'
): string => {
  const query = [
    receiptToken ? `receipt_token=${encodeURIComponent(receiptToken)}` : '',
    spot?.slotId ? `slotId=${encodeURIComponent(spot.slotId)}` : '',
    spot?.startTime ? `startTime=${encodeURIComponent(spot.startTime)}` : '',
    outcome ? `${outcome}=true` : '',
  ]
    .filter(Boolean)
    .join('&');
  return `/orders/${orderId}${query ? `?${query}` : ''}`;
};

// The booking email's calendar file for one class time, on the API's own host
// on purpose: an iPhone Home Screen web app opens another host in the
// system's sheet, which adds the event, while a file made in the page can
// leave the app on a screen with no way out (WebKit bug 290847).
export const classCalendarFileUrl = (
  orderId: string,
  icalToken: string,
  item: Pick<OrderItem, 'slot_id' | 'override_slot_id' | 'start_time'>
): string => {
  const params = new URLSearchParams({
    token: icalToken,
    startTime: new Date(item.start_time).toISOString(),
  });
  const slotId = getOccurrenceId(item);
  if (slotId) params.set('slotId', slotId);
  return `${API_URL}/public/calendar/orders/${encodeURIComponent(orderId)}.ics?${params}`;
};

// A link holder's receipt names no code, so the coupon type alone says the
// booking was paid with credits.
export const isCreditBooking = ({
  coupon_code,
  coupon_type,
  viewer,
}: Pick<Order, 'coupon_code' | 'coupon_type' | 'viewer'>): boolean =>
  coupon_type === 'CUSTOM' && (!!coupon_code || viewer === 'LINK_HOLDER');

// Recurring bookings carry slot_id; date-override bookings carry override_slot_id.
export const getOccurrenceId = (
  item: Pick<OrderItem, 'slot_id' | 'override_slot_id'>
): string | null => item.slot_id ?? item.override_slot_id ?? null;

type ClassTimeItem = Pick<
  OrderItem,
  | 'slot_id'
  | 'override_slot_id'
  | 'start_time'
  | 'end_time'
  | 'booking_status'
  | 'cancelled_at'
>;

export type ReceiptClassTime<T extends ClassTimeItem = OrderItem> = {
  slotId: string | null;
  startTime: string;
  items: T[];
  activeItems: T[];
  head: T;
};

export const isLiveItem = (item: { booking_status: string }): boolean =>
  item.booking_status !== 'CANCELLED';

export const instantOf = (time: string): number => new Date(time).getTime();

const groupBy = <T>(
  values: T[],
  keyOf: (value: T) => string | number
): T[][] => {
  const groups = new Map<string | number, T[]>();
  for (const value of values) {
    const key = keyOf(value);
    const group = groups.get(key);
    if (group) group.push(value);
    else groups.set(key, [value]);
  }
  return Array.from(groups.values());
};

// A spot cancelled before the backend stamped the time ranks oldest.
const cancelledAtOf = (item: ClassTimeItem): number =>
  item.cancelled_at ? instantOf(item.cancelled_at) : -Infinity;

// With nothing live, the spot cancelled last shows the class time as it ended:
// after a move, on the new slot with the instructor teaching it.
const toClassTime = <T extends ClassTimeItem>(
  items: T[]
): ReceiptClassTime<T> => {
  const activeItems = items.filter(isLiveItem);
  const cancelledItems = items.filter((item) => !isLiveItem(item));
  const head =
    activeItems[0] ??
    cancelledItems.reduce((latest, item) =>
      cancelledAtOf(item) > cancelledAtOf(latest) ? item : latest
    );
  return {
    slotId: getOccurrenceId(head),
    startTime: head.start_time,
    items: [...activeItems, ...cancelledItems],
    activeItems,
    head,
  };
};

// A class move leaves an order's cancelled spots on the old slot and puts its
// live ones on a new slot at the same time. So at one instant the cancelled
// spots belong to the one occurrence still holding live spots there, or with
// none, to each other; two live occurrences at one instant stay apart.
const joinCancelledSpots = <T extends ClassTimeItem>(
  occurrences: T[][]
): T[][] =>
  groupBy(occurrences, (group) => instantOf(group[0].start_time)).flatMap(
    (atInstant) =>
      atInstant.filter((group) => group.some(isLiveItem)).length > 1
        ? atInstant
        : [atInstant.flat()]
  );

// A weekly slot repeats every week, so a class time is its occurrence id and
// start instant together. Live spots come first, so a class time's first item
// is always one the backend can act on.
export const groupClassTimes = <T extends ClassTimeItem>(
  items: T[],
  joinCancelled: boolean
): ReceiptClassTime<T>[] => {
  const occurrences = groupBy(
    items,
    (item) => `${getOccurrenceId(item)}|${instantOf(item.start_time)}`
  );
  return (joinCancelled ? joinCancelledSpots(occurrences) : occurrences)
    .map((group) => toClassTime(group))
    .sort(
      (a, b) =>
        instantOf(a.startTime) - instantOf(b.startTime) ||
        (a.slotId ?? '').localeCompare(b.slotId ?? '')
    );
};

const holdsSlot = (
  classTime: ReceiptClassTime<ClassTimeItem>,
  slotId: string | null
): boolean =>
  !!slotId && classTime.items.some((item) => getOccurrenceId(item) === slotId);

const hasLiveSpots = (classTime: ReceiptClassTime<ClassTimeItem>): boolean =>
  classTime.activeItems.length > 0;

// The URL names the class time the customer was acting on: its slot and time
// pin it, the time alone still finds it after a move replaced the slot, and an
// older link naming only the slot gets that slot's next date. A receipt just
// cancelled keeps the cancelled spots apart, since a live spot at the same
// time is then another booking of hers.
export const selectReceiptItems = <T extends ClassTimeItem>(
  items: T[],
  url: { slotId: string | null; startTime: string | null },
  followLiveSpot: boolean,
  now: Date
): ReceiptClassTime<T> | null => {
  const classTimes = groupClassTimes(items, followLiveSpot);
  const urlInstant = url.startTime ? instantOf(url.startTime) : null;
  const atUrlTime = classTimes.filter(
    (classTime) => instantOf(classTime.startTime) === urlInstant
  );
  const candidates =
    [
      atUrlTime.filter((classTime) => holdsSlot(classTime, url.slotId)),
      atUrlTime,
      classTimes.filter((classTime) => holdsSlot(classTime, url.slotId)),
    ].find((list) => list.length > 0) ?? classTimes;
  const wanted = candidates.filter(
    (classTime) => hasLiveSpots(classTime) === followLiveSpot
  );
  return (
    wanted.find(
      (classTime) => instantOf(classTime.head.end_time) > now.getTime()
    ) ??
    wanted[0] ??
    candidates[0] ??
    null
  );
};

export const withReceiptClassTime = (
  search: string,
  classTime: Pick<ReceiptClassTime, 'slotId' | 'startTime'>
): string => {
  const params = new URLSearchParams(search);
  if (classTime.slotId) {
    params.set('slotId', classTime.slotId);
  } else {
    params.delete('slotId');
  }
  params.set('startTime', classTime.startTime);
  return params.toString();
};

// On a booking's cancel and reschedule an uncoded 400 means the page's copy of
// the order is out of date, in a sentence written for an operator: after a
// class move every future booking has new slot ids, so a receipt open across
// it names a spot the order no longer holds. On a reschedule's destination the
// same goes for the codes that say a time's ids were replaced, a time from a
// grid read before the move.
export const isStaleBookingRefusal = (error: unknown): boolean => {
  const code = getErrorCode(error);
  if (getErrorStatus(error) === 400 && code === 'Error') return true;
  return !!code && OCCURRENCE_REPLACED_CODES.includes(code);
};

// A reschedule's destination closed after the grid loaded. The booking is
// unchanged and the server's sentence says why the time cannot take it.
export const isClosedClassTimeRefusal = (error: unknown): boolean => {
  const code = getErrorCode(error);
  return (
    !!code &&
    OCCURRENCE_UNAVAILABLE_CODES.includes(code) &&
    !OCCURRENCE_REPLACED_CODES.includes(code)
  );
};

// A checkout that lost its class time is marked REFUNDED before the backend
// asks Stripe for the refund, and Stripe can refuse it. Only the email knows
// which happened, so this copy must be true either way.
const LOST_CLASS_TIME_TITLE = 'This class time is\nno longer available';

const PAYMENT_IN_EMAIL =
  'Check your email to see what happened to your payment.';

// The backend puts the gift card's whole share back on the card itself, so
// that part is certain, and the race email says it too. A card that no longer
// works keeps its share where nobody can spend it, so the email names nothing
// there and neither does this.
const withGiftCardPart = (
  order: Pick<Order, 'gift_card' | 'currency'> | undefined,
  rest: string
): string => {
  const giftCard = order?.gift_card;
  return order &&
    giftCard &&
    giftCard.applied > 0 &&
    refundsToGiftCard(giftCard)
    ? `${formatAmount(giftCard.applied, order.currency)} you paid by gift card is back on your gift card. ${rest}`
    : rest;
};

export const raceRefundToast = (
  orderType: OrderType,
  order?: Pick<Order, 'gift_card' | 'currency'>
) => ({
  title:
    orderType === 'class'
      ? LOST_CLASS_TIME_TITLE.replace('\n', ' ')
      : 'We could not complete your order',
  description:
    orderType === 'class'
      ? withGiftCardPart(order, PAYMENT_IN_EMAIL)
      : PAYMENT_IN_EMAIL,
});

export const RACE_REFUND_RECEIPT = {
  title: LOST_CLASS_TIME_TITLE,
  body: PAYMENT_IN_EMAIL,
};

// The receipt body of a checkout that lost the class time race: REFUNDED, no
// items, and the card's share as it was applied.
export const classRaceRefundNotice = (
  order: Pick<Order, 'gift_card' | 'currency'>
): string => withGiftCardPart(order, RACE_REFUND_RECEIPT.body);
